import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { OnboardingScreenEditRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { isDefined } from "@/helpers/util";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { onboardingScreenMutations } from "@/queries/onboarding-screens/mutations";
import { useOnboardingScreenQueries } from "@/queries/onboarding-screens/queryOptions";
import { useProjectQueries } from "@/queries/project/queryOptions";
import { routes } from "@/routes";

import { FirstOnboardingScreenState } from "./helper";
import { OnboardingScreenForm, type OnboardingScreenFormProps } from "./OnboardingScreenForm";

export function EditOnboardingScreenPage(): React.ReactNode {
  const slug = useSlug();
  const { id: onboardingScreenId } = useParams<{ id: string }>();
  const screenId = onboardingScreenId;
  const navigate = useNavigate();
  const { uploadFormImage, isUploadingImage } = useUploadImage();

  const projectQueries = useProjectQueries();
  const onboardingScreenQueries = useOnboardingScreenQueries();
  const {
    data: onboardingScreens,
    isPending: isPendingOnboardingScreens,
    error: onboardingScreensError,
  } = useQuery(onboardingScreenQueries.list());
  const {
    data: onboardingScreenDetails,
    isLoading: isLoadingOnboardingScreenDetails,
    error: onboardingScreenDetailsError,
  } = useQuery({
    ...onboardingScreenQueries.details({ onboardingScreenId: screenId || "" }),
    enabled: isDefined(screenId),
  });
  const {
    data: projectDetails,
    isPending: isLoadingProjectDetails,
    error: projectDetailsError,
  } = useQuery(projectQueries.details());
  const updateOnboardingScreenMutation = onboardingScreenMutations.useUpdateOnboardingScreen();

  const error = onboardingScreenDetailsError || projectDetailsError || onboardingScreensError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingOnboardingScreenDetails || isLoadingProjectDetails || isPendingOnboardingScreens;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  if (!screenId) {
    return <ErrorPage status={404} />;
  }

  if (!onboardingScreenDetails) {
    return <FullSizeLoader withPadding />;
  }

  const firstOnboardingScreen = onboardingScreens.at(0);
  let firstOnboardingScreenState = FirstOnboardingScreenState.CURRENT;
  if (firstOnboardingScreen && firstOnboardingScreen.id !== screenId) {
    firstOnboardingScreenState =
      firstOnboardingScreen.title || firstOnboardingScreen.subtitle
        ? FirstOnboardingScreenState.HAS_TEXT
        : FirstOnboardingScreenState.NO_TEXT;
  }

  const formProps: OnboardingScreenFormProps = {
    styling: {
      bgColor: projectDetails?.styling.navigationColor,
      textColor: projectDetails?.styling.textLightColor,
    },
    isEditMode: true,
    defaultValues: {
      title: onboardingScreenDetails.title,
      subtitle: onboardingScreenDetails.subtitle,
      image: [onboardingScreenDetails.image],
    },
    firstOnboardingScreenState,
    isSubmitting: isUploadingImage || updateOnboardingScreenMutation.isPending,
    async onSubmit(data) {
      if (data.image.length === 0) {
        throw new Error("Image not provided");
      }

      const formImage = data.image.at(0);
      if (!formImage) {
        throw new Error("Image not provided");
      }

      const image = await uploadFormImage(formImage);

      if (!image) {
        return;
      }

      const payload: OnboardingScreenEditRequest = {
        title: data.title,
        subtitle: data.subtitle,
        imageId: image.id,
      };

      await updateOnboardingScreenMutation.mutateAsync({
        onboardingScreenId: screenId,
        data: payload,
      });

      void navigate(routes.onboardingScreens.list({ slug }));
    },
  };

  return <OnboardingScreenForm {...formProps} />;
}
